/**
 * Statistical Analysis Engine for A/B Testing
 * Provides comprehensive statistical calculations, confidence intervals,
 * significance testing, and performance analysis
 */

export interface StatisticalResult {
  pValue: number;
  zScore: number;
  confidenceInterval: [number, number];
  isSignificant: boolean;
  effectSize: number;
  standardError: number;
  powerAnalysis: number;
  sampleSizeAdequacy: 'adequate' | 'marginal' | 'insufficient';
}

export interface PerformanceMetrics {
  conversionRate: number;
  sampleSize: number;
  conversions: number;
  confidenceInterval: [number, number];
  standardError: number;
}

export interface ComparisonResult {
  improvement: number;
  absoluteDifference: number;
  relativeRisk: number;
  oddsRatio: number;
  statistical: StatisticalResult;
  businessImpact: {
    estimatedLift: number;
    revenueImpact?: number;
    confidenceLevel: number;
  };
}

export interface SegmentAnalysis {
  segmentId: string;
  segmentName: string;
  control: PerformanceMetrics;
  variant: PerformanceMetrics;
  comparison: ComparisonResult;
  sampleSizeRatio: number;
  isReliable: boolean;
}

export interface TemporalAnalysis {
  timePoint: Date;
  control: PerformanceMetrics;
  variant: PerformanceMetrics;
  cumulativeComparison: ComparisonResult;
  trendDirection: 'improving' | 'declining' | 'stable';
  volatility: number;
}

export interface TestRecommendation {
  type: 'continue' | 'stop_winner' | 'stop_inconclusive' | 'extend_duration' | 'increase_traffic';
  confidence: number;
  reasoning: string;
  expectedOutcome?: string;
  suggestedActions: string[];
  riskAssessment: 'low' | 'medium' | 'high';
}

/**
 * Calculate statistical significance using two-proportion z-test
 */
export function calculateZTest(
  controlConversions: number,
  controlSample: number,
  variantConversions: number,
  variantSample: number
): StatisticalResult {
  const p1 = controlConversions / controlSample;
  const p2 = variantConversions / variantSample;
  
  // Pooled proportion
  const pooledP = (controlConversions + variantConversions) / (controlSample + variantSample);
  
  // Standard error
  const standardError = Math.sqrt(pooledP * (1 - pooledP) * (1/controlSample + 1/variantSample));
  
  // Z-score
  const zScore = (p2 - p1) / standardError;
  
  // P-value (two-tailed)
  const pValue = 2 * (1 - normalCDF(Math.abs(zScore)));
  
  // Effect size (Cohen's h)
  const effectSize = 2 * (Math.asin(Math.sqrt(p2)) - Math.asin(Math.sqrt(p1)));
  
  // Confidence interval for difference in proportions
  const seDiff = Math.sqrt((p1 * (1 - p1)) / controlSample + (p2 * (1 - p2)) / variantSample);
  const marginOfError = 1.96 * seDiff; // 95% confidence
  const difference = p2 - p1;
  const confidenceInterval: [number, number] = [
    (difference - marginOfError) * 100,
    (difference + marginOfError) * 100
  ];
  
  // Statistical power calculation
  const powerAnalysis = calculateStatisticalPower(p1, p2, controlSample, variantSample);
  
  // Sample size adequacy
  const minSampleSize = Math.min(controlSample, variantSample);
  const sampleSizeAdequacy: StatisticalResult['sampleSizeAdequacy'] = 
    minSampleSize >= 1000 ? 'adequate' :
    minSampleSize >= 500 ? 'marginal' : 'insufficient';
  
  return {
    pValue,
    zScore,
    confidenceInterval,
    isSignificant: pValue < 0.05,
    effectSize: Math.abs(effectSize),
    standardError,
    powerAnalysis,
    sampleSizeAdequacy
  };
}

/**
 * Calculate confidence interval for a single proportion
 */
export function calculateConfidenceInterval(
  conversions: number,
  sampleSize: number,
  confidenceLevel: number = 95
): [number, number] {
  const proportion = conversions / sampleSize;
  const zValue = getZValue(confidenceLevel);
  const standardError = Math.sqrt((proportion * (1 - proportion)) / sampleSize);
  const marginOfError = zValue * standardError;
  
  return [
    Math.max(0, (proportion - marginOfError) * 100),
    Math.min(100, (proportion + marginOfError) * 100)
  ];
}

/**
 * Perform comprehensive A/B test comparison
 */
export function compareVariants(
  controlConversions: number,
  controlSample: number,
  variantConversions: number,
  variantSample: number,
  revenuePerConversion?: number
): ComparisonResult {
  const controlRate = controlConversions / controlSample;
  const variantRate = variantConversions / variantSample;
  
  const improvement = ((variantRate - controlRate) / controlRate) * 100;
  const absoluteDifference = (variantRate - controlRate) * 100;
  
  // Risk metrics
  const relativeRisk = variantRate / controlRate;
  const oddsRatio = (variantConversions / (variantSample - variantConversions)) / 
                   (controlConversions / (controlSample - controlConversions));
  
  const statistical = calculateZTest(controlConversions, controlSample, variantConversions, variantSample);
  
  // Business impact calculation
  const estimatedLift = improvement;
  const revenueImpact = revenuePerConversion ? 
    (variantConversions - (controlRate * variantSample)) * revenuePerConversion : undefined;
  
  return {
    improvement,
    absoluteDifference,
    relativeRisk,
    oddsRatio,
    statistical,
    businessImpact: {
      estimatedLift,
      revenueImpact,
      confidenceLevel: statistical.isSignificant ? 95 : Math.round((1 - statistical.pValue) * 100)
    }
  };
}

/**
 * Analyze performance by segments
 */
export function analyzeSegments(
  segments: Array<{
    id: string;
    name: string;
    controlConversions: number;
    controlSample: number;
    variantConversions: number;
    variantSample: number;
  }>
): SegmentAnalysis[] {
  return segments.map(segment => {
    const control: PerformanceMetrics = {
      conversionRate: segment.controlConversions / segment.controlSample,
      sampleSize: segment.controlSample,
      conversions: segment.controlConversions,
      confidenceInterval: calculateConfidenceInterval(segment.controlConversions, segment.controlSample),
      standardError: Math.sqrt((segment.controlConversions / segment.controlSample) * 
                              (1 - segment.controlConversions / segment.controlSample) / segment.controlSample)
    };
    
    const variant: PerformanceMetrics = {
      conversionRate: segment.variantConversions / segment.variantSample,
      sampleSize: segment.variantSample,
      conversions: segment.variantConversions,
      confidenceInterval: calculateConfidenceInterval(segment.variantConversions, segment.variantSample),
      standardError: Math.sqrt((segment.variantConversions / segment.variantSample) * 
                              (1 - segment.variantConversions / segment.variantSample) / segment.variantSample)
    };
    
    const comparison = compareVariants(
      segment.controlConversions,
      segment.controlSample,
      segment.variantConversions,
      segment.variantSample
    );
    
    const sampleSizeRatio = Math.min(segment.controlSample, segment.variantSample) / 
                           Math.max(segment.controlSample, segment.variantSample);
    
    const isReliable = Math.min(segment.controlSample, segment.variantSample) >= 100 && 
                      sampleSizeRatio >= 0.5;
    
    return {
      segmentId: segment.id,
      segmentName: segment.name,
      control,
      variant,
      comparison,
      sampleSizeRatio,
      isReliable
    };
  });
}

/**
 * Analyze temporal performance trends
 */
export function analyzeTemporalTrends(
  timeSeriesData: Array<{
    date: Date;
    controlConversions: number;
    controlSample: number;
    variantConversions: number;
    variantSample: number;
  }>
): TemporalAnalysis[] {
  let cumulativeControlConversions = 0;
  let cumulativeControlSample = 0;
  let cumulativeVariantConversions = 0;
  let cumulativeVariantSample = 0;
  
  return timeSeriesData.map((dataPoint, index) => {
    cumulativeControlConversions += dataPoint.controlConversions;
    cumulativeControlSample += dataPoint.controlSample;
    cumulativeVariantConversions += dataPoint.variantConversions;
    cumulativeVariantSample += dataPoint.variantSample;
    
    const control: PerformanceMetrics = {
      conversionRate: cumulativeControlConversions / cumulativeControlSample,
      sampleSize: cumulativeControlSample,
      conversions: cumulativeControlConversions,
      confidenceInterval: calculateConfidenceInterval(cumulativeControlConversions, cumulativeControlSample),
      standardError: Math.sqrt((cumulativeControlConversions / cumulativeControlSample) * 
                              (1 - cumulativeControlConversions / cumulativeControlSample) / cumulativeControlSample)
    };
    
    const variant: PerformanceMetrics = {
      conversionRate: cumulativeVariantConversions / cumulativeVariantSample,
      sampleSize: cumulativeVariantSample,
      conversions: cumulativeVariantConversions,
      confidenceInterval: calculateConfidenceInterval(cumulativeVariantConversions, cumulativeVariantSample),
      standardError: Math.sqrt((cumulativeVariantConversions / cumulativeVariantSample) * 
                              (1 - cumulativeVariantConversions / cumulativeVariantSample) / cumulativeVariantSample)
    };
    
    const cumulativeComparison = compareVariants(
      cumulativeControlConversions,
      cumulativeControlSample,
      cumulativeVariantConversions,
      cumulativeVariantSample
    );
    
    // Determine trend direction
    let trendDirection: TemporalAnalysis['trendDirection'] = 'stable';
    if (index >= 2) {
      const recentImprovement = cumulativeComparison.improvement;
      const previousData = timeSeriesData.slice(0, index - 1);
      const previousCumulativeComparison = compareVariants(
        previousData.reduce((sum, d) => sum + d.controlConversions, 0),
        previousData.reduce((sum, d) => sum + d.controlSample, 0),
        previousData.reduce((sum, d) => sum + d.variantConversions, 0),
        previousData.reduce((sum, d) => sum + d.variantSample, 0)
      );
      
      const improvementChange = recentImprovement - previousCumulativeComparison.improvement;
      if (Math.abs(improvementChange) > 2) {
        trendDirection = improvementChange > 0 ? 'improving' : 'declining';
      }
    }
    
    // Calculate volatility (coefficient of variation of daily rates)
    const dailyRates = timeSeriesData.slice(0, index + 1).map(d => 
      (d.variantConversions / d.variantSample) - (d.controlConversions / d.controlSample)
    );
    const meanRate = dailyRates.reduce((sum, rate) => sum + rate, 0) / dailyRates.length;
    const variance = dailyRates.reduce((sum, rate) => sum + Math.pow(rate - meanRate, 2), 0) / dailyRates.length;
    const volatility = Math.sqrt(variance) / Math.abs(meanRate) || 0;
    
    return {
      timePoint: dataPoint.date,
      control,
      variant,
      cumulativeComparison,
      trendDirection,
      volatility: Math.min(volatility, 1) // Cap at 1 for display purposes
    };
  });
}

/**
 * Generate test recommendations based on current performance
 */
export function generateRecommendations(
  comparison: ComparisonResult,
  temporalAnalysis: TemporalAnalysis[],
  testDuration: number, // days
  targetDuration: number, // days
  minSampleSize: number = 1000
): TestRecommendation[] {
  const recommendations: TestRecommendation[] = [];
  const latestAnalysis = temporalAnalysis.length > 0 ? temporalAnalysis[temporalAnalysis.length - 1] : null;
  const currentSampleSize = latestAnalysis 
    ? Math.min(latestAnalysis.control.sampleSize, latestAnalysis.variant.sampleSize)
    : minSampleSize; // Use minSampleSize as fallback
  
  // Check for statistical significance
  if (comparison.statistical.isSignificant) {
    if (Math.abs(comparison.improvement) > 5) {
      recommendations.push({
        type: 'stop_winner',
        confidence: 0.9,
        reasoning: `Test has reached statistical significance with ${comparison.improvement.toFixed(1)}% improvement`,
        expectedOutcome: `Implementing the ${comparison.improvement > 0 ? 'variant' : 'control'} could improve performance`,
        suggestedActions: [
          'Implement the winning variant',
          'Monitor performance post-implementation',
          'Plan follow-up tests for further optimization'
        ],
        riskAssessment: 'low'
      });
    }
  } else {
    // Check if test should continue
    if (testDuration < targetDuration && currentSampleSize < minSampleSize * 2) {
      recommendations.push({
        type: 'continue',
        confidence: 0.8,
        reasoning: 'Test has not reached statistical significance and more data is needed',
        expectedOutcome: 'Continued testing may reveal significant differences',
        suggestedActions: [
          'Continue test for planned duration',
          'Monitor daily for significant changes',
          'Consider increasing traffic allocation if possible'
        ],
        riskAssessment: 'low'
      });
    } else if (testDuration >= targetDuration) {
      recommendations.push({
        type: 'stop_inconclusive',
        confidence: 0.7,
        reasoning: 'Test has run for planned duration without reaching significance',
        expectedOutcome: 'No meaningful difference between variants',
        suggestedActions: [
          'End test and keep current version',
          'Analyze learnings for future tests',
          'Consider testing different elements'
        ],
        riskAssessment: 'medium'
      });
    }
  }
  
  // Check for sample size issues
  if (currentSampleSize < minSampleSize) {
    recommendations.push({
      type: 'increase_traffic',
      confidence: 0.8,
      reasoning: 'Sample size is below recommended minimum for reliable results',
      expectedOutcome: 'Increased traffic will improve statistical power',
      suggestedActions: [
        'Increase traffic allocation to test',
        'Extend test duration if needed',
        'Consider broader audience targeting'
      ],
      riskAssessment: 'medium'
    });
  }
  
  // Check for trend analysis
  if (temporalAnalysis.length > 7) {
    const recentTrend = temporalAnalysis.slice(-3);
    const isImproving = recentTrend.every(t => t.trendDirection === 'improving');
    const isDeclining = recentTrend.every(t => t.trendDirection === 'declining');
    
    if (isImproving && !comparison.statistical.isSignificant) {
      recommendations.push({
        type: 'extend_duration',
        confidence: 0.7,
        reasoning: 'Performance is showing improving trend, may reach significance with more time',
        expectedOutcome: 'Extended testing may reveal significant improvement',
        suggestedActions: [
          'Extend test duration by 3-5 days',
          'Monitor trend continuation',
          'Prepare for early stopping if significance is reached'
        ],
        riskAssessment: 'low'
      });
    } else if (isDeclining && comparison.improvement < 0) {
      recommendations.push({
        type: 'stop_winner',
        confidence: 0.8,
        reasoning: 'Variant is consistently underperforming with declining trend',
        expectedOutcome: 'Control version is likely the better choice',
        suggestedActions: [
          'Stop test and keep control version',
          'Analyze why variant underperformed',
          'Plan new test with different approach'
        ],
        riskAssessment: 'low'
      });
    }
  }
  
  return recommendations.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Generate automated insights from test results
 */
export function generateInsights(
  comparison: ComparisonResult,
  segmentAnalysis: SegmentAnalysis[],
  temporalAnalysis: TemporalAnalysis[]
): Array<{
  type: 'performance' | 'statistical' | 'segment' | 'temporal' | 'business';
  title: string;
  description: string;
  confidence: number;
  actionable: boolean;
  data?: any;
}> {
  const insights = [];
  
  // Performance insights
  if (comparison.statistical.isSignificant) {
    insights.push({
      type: 'performance' as const,
      title: `${comparison.improvement > 0 ? 'Significant Improvement' : 'Significant Decline'} Detected`,
      description: `The variant shows a ${Math.abs(comparison.improvement).toFixed(1)}% ${comparison.improvement > 0 ? 'improvement' : 'decline'} with ${comparison.businessImpact.confidenceLevel}% confidence`,
      confidence: comparison.businessImpact.confidenceLevel / 100,
      actionable: true,
      data: { improvement: comparison.improvement, pValue: comparison.statistical.pValue }
    });
  }
  
  // Statistical power insights
  if (comparison.statistical.powerAnalysis < 80) {
    insights.push({
      type: 'statistical' as const,
      title: 'Low Statistical Power Detected',
      description: `Current test has ${comparison.statistical.powerAnalysis.toFixed(0)}% power. Consider increasing sample size for more reliable results`,
      confidence: 0.9,
      actionable: true,
      data: { power: comparison.statistical.powerAnalysis }
    });
  }
  
  // Segment-specific insights
  const significantSegments = segmentAnalysis.filter(s => s.comparison.statistical.isSignificant);
  if (significantSegments.length > 0 && significantSegments.length < segmentAnalysis.length) {
    insights.push({
      type: 'segment' as const,
      title: 'Segment-Specific Performance Detected',
      description: `${significantSegments.length} out of ${segmentAnalysis.length} segments show significant differences. Consider targeted approaches`,
      confidence: 0.8,
      actionable: true,
      data: { significantSegments: significantSegments.map(s => s.segmentName) }
    });
  }
  
  // Temporal insights
  if (temporalAnalysis.length > 5) {
    const recentVolatility = temporalAnalysis.slice(-5).reduce((sum, t) => sum + t.volatility, 0) / 5;
    if (recentVolatility > 0.3) {
      insights.push({
        type: 'temporal' as const,
        title: 'High Performance Volatility',
        description: `Recent performance shows high volatility (${(recentVolatility * 100).toFixed(0)}%). Results may be less reliable`,
        confidence: 0.7,
        actionable: true,
        data: { volatility: recentVolatility }
      });
    }
  }
  
  // Business impact insights
  if (comparison.businessImpact.revenueImpact) {
    const impact = comparison.businessImpact.revenueImpact;
    insights.push({
      type: 'business' as const,
      title: `Estimated Revenue Impact: ${impact > 0 ? '+' : ''}$${Math.abs(impact).toLocaleString()}`,
      description: `Based on current performance, implementing the ${comparison.improvement > 0 ? 'variant' : 'control'} could ${impact > 0 ? 'increase' : 'decrease'} revenue`,
      confidence: comparison.businessImpact.confidenceLevel / 100,
      actionable: true,
      data: { revenueImpact: impact }
    });
  }
  
  return insights.sort((a, b) => b.confidence - a.confidence);
}

// Helper functions

function normalCDF(x: number): number {
  // Approximation of the cumulative distribution function for standard normal distribution
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989423 * Math.exp(-x * x / 2);
  let prob = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  
  if (x > 0) {
    prob = 1 - prob;
  }
  
  return prob;
}

function getZValue(confidenceLevel: number): number {
  const zValues: { [key: number]: number } = {
    90: 1.645,
    95: 1.96,
    99: 2.576
  };
  return zValues[confidenceLevel] || 1.96;
}

function calculateStatisticalPower(
  p1: number,
  p2: number,
  n1: number,
  n2: number,
  _alpha: number = 0.05
): number {
  // Simplified power calculation for two-proportion test
  const pooledP = (p1 * n1 + p2 * n2) / (n1 + n2);
  const se1 = Math.sqrt(pooledP * (1 - pooledP) * (1/n1 + 1/n2));
  const se2 = Math.sqrt(p1 * (1 - p1) / n1 + p2 * (1 - p2) / n2);
  
  const zAlpha = getZValue(95); // 1.96 for alpha = 0.05
  const zBeta = (Math.abs(p2 - p1) - zAlpha * se1) / se2;
  
  const power = normalCDF(zBeta) * 100;
  return Math.max(0, Math.min(100, power));
}